import dynamic from 'next/dynamic';

import type { SerializedBlock } from '@zoomit/editor';

import { OriginProvider } from '../../providers/OriginProvider';
import EditorParserWithElementAndLeaf from '../editor-parser/EditorParserWithElementAndLeaf';
import type { ActionBoxBlockProps } from './action-box/ActionBoxBlock';
import { ActionBoxBlock } from './action-box/ActionBoxBlock';
import AttractionBlock from './attraction/AttractionBlock';
import BuyingGuideBlock from './buying-guide/BuyingGuideBlock';
import RelatedProductBlock from './related-product/RelatedProductBlock';
import ReviewBlock from './review/ReviewBlock';

const AudioBlock = dynamic(() => import('./audio/AudioBlock'));
const ButtonBlock = dynamic(() => import('./button/ButtonBlock'));
const ProductComparisonTableBlock = dynamic(
  () => import('./product-comparison-table/ProductComparisonTableBlock'),
);
const CodeBlock = dynamic(() => import('./code/CodeBlock'));
const PollBlock = dynamic(() => import('./poll/PollBlock'));
const CommonQuestionBlock = dynamic(() => import('./common-question/CommonQuestionBlock'));
const DividerBlock = dynamic(() => import('./divider/DividerBlock'));
const DocumentBlock = dynamic(() => import('./document/DocumentBlock'));
const ImageComparisonBlock = dynamic(() => import('./image-comparison/ImageComparisonBlock'));
const KeywordBlock = dynamic(() => import('./keyword/KeywordBlock'));
const NotificationBoxBlock = dynamic(() => import('./notification-box/NotificationBoxBlock'));
const ImageBlock = dynamic(() => import('./image/ImageBlock'));
const TutorialBlock = dynamic(() => import('./tutorial/TutorialBlock'));
const TableBlock = dynamic(() => import('./table/TableBlock'));
const ProsConsBlock = dynamic(() => import('./pros-cons/ProsConsBlock'));
const QuoteBlock = dynamic(() => import('./quote/QuoteBlock'));
const ShortVideoBlock = dynamic(() => import('./short-video/ShortVideoBlock'));
const TableOfContent = dynamic(() => import('./table-of-content/TableOfContent'));
const TextImageBlock = dynamic(() => import('./text-image/TextImageBlock'));
const VideoIframeBlock = dynamic(() => import('./video-iframe/VideoIframeBlock'));
const VideoBlock = dynamic(() => import('./video/VideoBlock'));
const RelatedArticleBlock = dynamic(() => import('./related-article/RelatedArticleBlock'));
const QuizBlock = dynamic(() => import('./quiz/QuizBlock'));

// TODO: fix any
export const renderRemoteBlock = (item: SerializedBlock, hasSideBar?: boolean) => {
  const { type, id } = item;

  switch (type) {
    case 'audio': {
      return <AudioBlock key={id} blockData={item.data} />;
    }
    case 'button': {
      return <ButtonBlock key={id} blockData={item.data} />;
    }
    case 'code': {
      return <CodeBlock key={id} blockData={item.data} />;
    }
    case 'common-question': {
      return <CommonQuestionBlock key={id} blockData={item.data} />;
    }
    case 'divider': {
      return <DividerBlock key={id} />;
    }
    case 'document': {
      return <DocumentBlock key={id} blockData={item.data} />;
    }
    case 'image-comparison': {
      return <ImageComparisonBlock key={id} blockData={item.data} />;
    }
    case 'keyword': {
      return <KeywordBlock key={id} blockData={item.data} template={item.template as any} />;
    }
    case 'notification-box': {
      return <NotificationBoxBlock key={id} blockData={item.data} />;
    }
    case 'image': {
      return (
        <ImageBlock
          key={id}
          blockData={item.data}
          template={item.template as any}
          id={item.id}
          hasSideBar={hasSideBar}
        />
      );
    }
    case 'tutorial': {
      return <TutorialBlock key={id} blockData={item.data} />;
    }
    case 'table': {
      return <TableBlock key={id} blockData={item.data} />;
    }
    case 'pros-cons': {
      return <ProsConsBlock key={id} blockData={item.data} />;
    }
    case 'quote': {
      return <QuoteBlock key={id} blockData={item.data} />;
    }
    case 'short-video': {
      return <ShortVideoBlock key={id} blockData={item.data} />;
    }
    case 'table-of-content': {
      return <TableOfContent key={id} headings={item.data || []} isCollapsed={!!item.isCollapsed} />;
    }
    case 'related-article': {
      return <RelatedArticleBlock key={id} blockData={item.data} template={item.template as any} />;
    }
    case 'text-image': {
      return <TextImageBlock key={id} blockData={item.data} template={item.template as any} />;
    }
    case 'video-iframe': {
      return <VideoIframeBlock key={id} blockData={item.data} />;
    }
    case 'video-metadata': {
      return <VideoBlock key={id} blockData={item.data} />;
    }
    case 'typography': {
      return <EditorParserWithElementAndLeaf key={id} data={item.data} id={id} />;
    }
    case 'poll': {
      return <PollBlock key={id} blockData={item.data} />;
    }
    case 'quiz': {
      return <QuizBlock key={id} blockData={item.data} />;
    }
    case 'review': {
      return (
        <ReviewBlock
          template={item.template as any}
          hasSidebar={Boolean(hasSideBar)}
          key={id}
          blockData={item.data}
        />
      );
    }
    case 'action-box': {
      return (
        <ActionBoxBlock
          key={item.id}
          blockData={item.data}
          template={item.template as ActionBoxBlockProps['template']}
        />
      );
    }

    case 'attraction': {
      return <AttractionBlock blockData={item.data} />;
    }
    // Catalog blocks
    case 'related-product': {
      return (
        <OriginProvider key={id} origin="Catalog">
          <RelatedProductBlock blockData={item.data} template={item.template as any} blockId={item.id} />
        </OriginProvider>
      );
    }
    case 'compare-product-table': {
      return (
        <OriginProvider key={id} origin="Catalog">
          <ProductComparisonTableBlock blockData={item.data} />
        </OriginProvider>
      );
    }
    case 'buying-guide': {
      return (
        <OriginProvider key={id} origin="Catalog">
          <BuyingGuideBlock blockData={item.data} />
        </OriginProvider>
      );
    }

    default:
      return <></>;
  }
};
